import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, Mail, Menu, X, ChevronDown } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { companyInfo, navLinks, brand } from '../mock/mock';
import { useQuote } from './QuoteContext';

const NavItem = ({ link, onClick }) => {
  const isExternal = link.href.startsWith('/') && !link.href.startsWith('/#');
  const className = 'px-4 py-2 text-sm font-medium text-slate-700 hover:text-sky-600 transition-colors relative group rounded-lg hover:bg-sky-50';
  if (isExternal) return <Link to={link.href} onClick={onClick} className={className}>{link.label}</Link>;
  return <a href={link.href} onClick={onClick} className={className}>{link.label}</a>;
};

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { openQuote } = useQuote();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location]);

  return (
    <>
      {/* Top utility bar */}
      <div className="hidden md:block bg-gradient-to-r from-sky-600 via-blue-600 to-indigo-700 text-white text-xs">
        <div className="container-x px-6 py-2 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <a href={`tel:${companyInfo.supportPhone}`} className="flex items-center gap-2 hover:text-cyan-200 transition-colors">
              <Phone size={12} /> Support: {companyInfo.supportPhone}
            </a>
            <a href={`tel:${companyInfo.salesPhone}`} className="flex items-center gap-2 hover:text-cyan-200 transition-colors">
              <Phone size={12} /> Sales: {companyInfo.salesPhone}
            </a>
            <a href={`mailto:${companyInfo.email}`} className="flex items-center gap-2 hover:text-cyan-200 transition-colors">
              <Mail size={12} /> {companyInfo.email}
            </a>
          </div>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1.5">
              <span className="relative flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-300 opacity-75" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
              </span>
              <span className="text-emerald-200">All systems operational</span>
            </span>
            <Link to="/contact-us" className="px-3 py-1 rounded bg-white/15 text-white hover:bg-white/25 transition-colors">Support Portal</Link>
          </div>
        </div>
      </div>

      <motion.nav
        initial={{ y: -40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.6 }}
        className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-xl shadow-lg border-b border-sky-100' : 'bg-white/90 backdrop-blur-md border-b border-sky-50'}`}
      >
        <div className="container-x px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <img src={brand.logo} alt="DC10G Cloud" className="h-12 w-auto object-contain group-hover:scale-105 transition-transform" />
          </Link>

          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((l) => (<NavItem key={l.label} link={l} />))}
          </div>

          <div className="hidden lg:flex items-center gap-3">
            <a href={`tel:${companyInfo.salesPhone}`} className="text-sm font-semibold text-slate-700 hover:text-sky-600 flex items-center gap-1.5">
              <Phone size={14} /> {companyInfo.salesPhone}
            </a>
            <button onClick={() => openQuote({ title: 'Get a Quote', subtitle: 'Share a few details and our team will reach out within 24 hours.' })} className="btn-primary text-sm">Get a Quote</button>
          </div>

          <button onClick={() => setOpen(!open)} className="lg:hidden text-slate-800">
            {open ? <X /> : <Menu />}
          </button>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="lg:hidden bg-white border-t border-slate-100 overflow-hidden">
              <div className="px-6 py-4 flex flex-col gap-2">
                {navLinks.map((l) => (<NavItem key={l.label} link={l} onClick={() => setOpen(false)} />))}
                <button onClick={() => { setOpen(false); openQuote({ title: 'Get a Quote' }); }} className="btn-primary text-center mt-2 justify-center">Get a Quote</button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>
    </>
  );
};

export default Navbar;
